import React, { createContext, useEffect, useState, useRef } from "react";
import "../process.scss";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { Dialog } from "primereact/dialog";

import { getName } from "country-list";
import ReactCountryFlag from "react-country-flag";
import StatusBadge from "./StatusBadge";
import useProcess from "../actions/useProcess";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import ProcessLayout from "./ProcessLayout";
import { useSelector } from "react-redux";
import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";
import { OverlayPanel } from "primereact/overlaypanel";
import NewRequestModal from "../../Requests/components/new-request-modal/NewRequestModal";
import useRequest, { REQUEST_TYPES } from "../../Requests/useRequest";
import AuditSidebar from "../../../components/audit-sidebar/AuditSidebar";
import API_ROUTES from "../../../api-routes";
import apiService from "../../../services/service";

import EscalationMessage from "./EscalationMessage";
import CustomeButton from "~/components/Button";
import AdminOnly from "~/components/AdminOnly";
import { format, parseISO } from "date-fns";
import ErrorToast from "~/components/toast-notification/ErrorToast";

export const ProcessContext = createContext();

const ProcessLayoutWrapper = ({ children }) => {
  const { role } = useSelector((state) => state.login);
  const {
    process,
    milestones,
    milestonesLoading,
    formatDate,
    fetchProcessAttachments,
    fetchProcessById,
    isAuditSidebarOpen,
    setIsAuditSidebarOpen,
    showRequestModal,
    setShowRequestModal,
    hideRequestModal,
    statesAttachment,
    handleDeleteDocument,
    handleConfirmDeleteDocumment,
    handleRestoreDocument,
    setStatesAttachment,
    fetchPayslipMatching,
    paySlipMatchingResult,
    loading,
    handleDeletePayslip,
    handleUndoPayslip,
    isPayrollPlus,
    payslipUrl,
    fetchReleasedPayslips,
    selectedItems,
    filterData,
    setFilterData,
    removeFnRef,
    handleRemoveFilter,
    handleFilterChange,
    filterLoading,
    onSortOrderChange,
    allData,
    filterPageChange,
    currentPage,
  } = useProcess();

  const auditLabels = () => {
    return {
      create: "Entity Created",
      update: "Information Updated",
      delete: "Entity Removed",
    };
  };

  const actionsList = useRef(null);
  const messagesRef = useRef(null);
  const { value: isEscalationsFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.ESCALATIONS,
    false,
  );

  const navigate = useNavigate();
  const location = useLocation();
  const { value: isDocumentStorageFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.DOCUMENT_STORAGE,
  );
  const { value: isMessagingFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.MESSAGING,
  );

  const { value: isMyDashboardFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.MY_DASHBOARD,
    false,
  );

  const contextMenu = useRef(null);
  const [showDeleteProcessModal, setShowDeleteProcessModal] = useState(false);
  const [isDeletingProcess, setIsDeletingProcess] = useState(false);
  const [showDeleteError, setShowDeleteError] = useState(false);

  const handleConfirmDeleteProcess = async () => {
    setIsDeletingProcess(true);
    try {
      // If the browser is offline, treat as failure immediately
      if (
        typeof navigator !== "undefined" &&
        navigator &&
        navigator.onLine === false
      ) {
        throw new Error("Offline - cannot delete process");
      }

      const res = await apiService(API_ROUTES.DELETE_PROCESS, "DELETE", {
        replace: { id },
      });

      // apiService returns an object for 401 (unauthorized) instead of throwing.
      // Also, if an explicit status=false is returned, treat as failure.
      if (res && (res.unauthorized || res.status === false)) {
        throw new Error("Delete failed");
      }

      const entityId =
        process?.entityData?.id || process?.entity || process?.entityId;
      setShowDeleteProcessModal(false);
      if (entityId) {
        const yyyymm = getDeadlineDate() || "";
        navigate(`/entity/${entityId}/calendar`, {
          state: {
            deleteMsg: `${process?.name}${yyyymm ? ` ${yyyymm}` : ""} process has been deleted.`,
          },
        });
      } else {
        navigate(isMyDashboardFeatureEnabled ? "/my-dashboard" : "/");
      }
    } catch (error) {
      console.error("Error deleting process:", error);
      setShowDeleteProcessModal(false);
      setShowDeleteError(true);
    } finally {
      setIsDeletingProcess(false);
    }
  };
  const tabs = [
    ...(isMessagingFeatureEnabled
      ? [
          { name: "Internal Conversations", route: "conversations/internal" },
          { name: "Public Conversations", route: "conversations/public" },
        ]
      : []),
    { name: "Milestones", route: "milestones" },
    ...(isDocumentStorageFeatureEnabled
      ? [{ name: "Attachments", route: "attachments" }]
      : []),
  ];
  const activeIndex = tabs.findIndex((tab) =>
    location.pathname.includes(tab.route),
  );
  const [selectedIndex, setSelectedIndex] = useState(
    activeIndex !== -1 ? activeIndex : 2,
  );
  const [activeEscalation, setActiveEscalation] = useState(null);
  const [showMessage, setShowMessage] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    setSelectedIndex(activeIndex !== -1 ? activeIndex : 2);
  }, [location.pathname]);

  useEffect(() => {
    if (
      isEscalationsFeatureEnabled &&
      activeEscalation &&
      !["LSP_USER", "LSP_MANAGER"].includes(role)
    ) {
      if (activeEscalation?.status !== "completed") {
        setShowMessage(true);
      } else {
        setShowMessage(false);
      }
    } else {
      setShowMessage(false);
    }
  }, [activeEscalation?.id, activeEscalation?.status]);
  useEffect(() => {
    if (process?.escalation) {
      setActiveEscalation(process?.escalation);
    }
  }, [process?.escalation]);

  const getHeaderName = () => {
    return (
      <p className="title">
        {isPayrollPlus
          ? `${getDeadlineDatePayrollPlus()} ${process?.name}`
          : `${process?.name} ${getDeadlineDate()}${getSequenceNumber()}`}
        {process.numberof_flagged_milestones > 0 && (
          <i className="pi pi-flag-fill text-warning ml-2 flag-red-icon"></i>
        )}
      </p>
    );
  };

  const getDeadlineDate = () => {
    if (process?.start_date) {
      const deadline =
        new Date(process?.start_date).getFullYear() +
        new Date(process?.start_date).toLocaleDateString("default", {
          month: "2-digit",
        });
      return deadline;
    }
  };
  const getDeadlineDatePayrollPlus = () => {
    if (process?.start_date) {
      const date = parseISO(process.start_date);
      return format(date, "MMMM yyyy");
    }
    return "";
  };

  const getSequenceNumber = () => {
    if (process?.sequence) {
      return `_${process?.sequence}`;
    }
    return "";
  };

  const headerTemplate = (options) => {
    return (
      <div
        className={`${options.route} header-template-container`}
        onClick={() => {
          options.onClick();
          navigate(`/process/${id}/${options?.route}`);
        }}
      >
        <p className={options?.selected ? "tab-selected" : "header-value-name"}>
          {options?.name}
        </p>
      </div>
    );
  };

  const timelineMarkerTemplate = (item) => {
    return !milestonesLoading ? (
      <StatusBadge status={item?.statusData} />
    ) : (
      <Skeleton width="20px" height="20px" shape="circle" />
    );
  };

  const timelineContentTemplate = (item) => {
    return (
      <div className="sidebar-timeline-content-template">
        <p className="sidebar-timeline-content-label">{item?.name}</p>
        <p className="sidebar-timeline-content-status">{`(${item?.status})`}</p>
        <p className="sidebar-timeline-content-date">
          {formatDate(item?.due_date)}
        </p>
      </div>
    );
  };

  const getTabVisibility = (tab) => {
    if (tab["name"] === "Internal Conversations") {
      return [
        "GXM_ADMIN",
        "LSP_USER",
        "GXM_USER",
        "LSP_MANAGER",
        "GXM_MANAGER",
      ].includes(role);
    }
    return true;
  };

  return (
    <ProcessLayout
      process={process}
      milestones={milestones}
      milestonesLoading={milestonesLoading}
      formatDate={formatDate}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
      tabs={tabs}
    >
      <ProcessContext.Provider
        value={{
          process,
          milestones,
          formatDate,
          milestonesLoading,
          fetchProcessAttachments,
          statesAttachment,
          handleDeleteDocument,
          handleConfirmDeleteDocumment,
          handleRestoreDocument,
          setStatesAttachment,
          fetchPayslipMatching,
          paySlipMatchingResult,
          loading,
          handleDeletePayslip,
          handleUndoPayslip,
          fetchReleasedPayslips,
          selectedItems,
          filterData,
          setFilterData,
          removeFnRef,
          handleRemoveFilter,
          handleFilterChange,
          filterLoading,
          onSortOrderChange,
          allData,
          filterPageChange,
          currentPage,
        }}
      >
        <div className="process-layout-section">
          <div className="process-layout-container">
            <div className="header-container">
              <div className="title-container">
                {process ? (
                  getHeaderName()
                ) : (
                  <Skeleton width="600px" height="20px" className="mr-1" />
                )}
              </div>
              <div className="actions-container">
                <div className="status-container">
                  <p className="status">Status</p>
                  {process ? (
                    <Button outlined>
                      <StatusBadge status={process?.statusData} />
                      {process?.statusData?.name}
                    </Button>
                  ) : (
                    <Skeleton width="200px" height="40px" className="mr-1" />
                  )}
                </div>
                {process?.serviceData?.name !== "Payroll +" ? (
                  <div className="flex">
                    {["CUSTOMER", "CUSTOMER_MANAGER"].includes(role) && (
                      <CustomeButton
                        label="Escalate Process"
                        className="w-max"
                        variant="contained"
                        disabled={activeEscalation}
                        onClick={() => {
                          setShowRequestModal(true);
                        }}
                      />
                    )}
                    <AdminOnly>
                      <Menu
                        className="global-entities-context-menu"
                        popup
                        ref={contextMenu}
                        id="process_actions_menu"
                        model={[
                          {
                            label: (
                              <div className="global-entities-context-menu-item">
                                View History
                                <i className="pi pi-history" />
                              </div>
                            ),
                            command: () => setIsAuditSidebarOpen(true),
                          },
                          {
                            label: (
                              <div className="global-entities-context-menu-item">
                                Delete Process
                                <i className="pi pi-trash" />
                              </div>
                            ),
                            command: () => setShowDeleteProcessModal(true),
                          },
                        ]}
                      />
                      <Button
                        onClick={(event) => contextMenu.current.toggle(event)}
                        icon="pi pi-ellipsis-v"
                        className="menu__toggle"
                      />
                    </AdminOnly>
                  </div>
                ) : (
                  <div className="flex">
                    {["CUSTOMER", "CUSTOMER_MANAGER"].includes(role) && (
                      <CustomeButton
                        label="Escalate Process"
                        className="w-max"
                        disabled={activeEscalation}
                        onClick={() => {
                          setShowRequestModal(true);
                        }}
                      />
                    )}
                    <AdminOnly>
                      <CustomeButton
                        label="View History"
                        className="w-max"
                        onClick={() => setIsAuditSidebarOpen(true)}
                      />
                    </AdminOnly>
                    <CustomeButton
                      label="Open in Payslip"
                      onClick={() => {
                        window.open(payslipUrl);
                      }}
                      className="w-max"
                      variant="contained"
                      icon="pi pi-external-link"
                      iconPos="right"
                      disabled={!payslipUrl}
                    />
                  </div>
                )}
              </div>
            </div>
            {showMessage && (
              <EscalationMessage
                role={role}
                activeEscalation={activeEscalation}
              />
            )}
            <div className="tab-container">
              <TabView
                activeIndex={selectedIndex}
                onTabChange={(e) => {
                  setSelectedIndex(e.index);
                }}
              >
                {tabs.map((tab, index) => {
                  return (
                    <TabPanel
                      visible={getTabVisibility(tab)}
                      key={index}
                      headerTemplate={(options) =>
                        headerTemplate({
                          ...options,
                          name: tab.name,
                          route: tab.route,
                        })
                      }
                    ></TabPanel>
                  );
                })}
              </TabView>
              <Outlet />
            </div>
          </div>
        </div>

        {isAuditSidebarOpen && (
          <AuditSidebar
            heading=""
            endpoint={API_ROUTES.FETCH_PROCESS_AUDIT}
            id={id}
            labels={auditLabels}
            setSidebarToggle={setIsAuditSidebarOpen}
            sidebarToggle={isAuditSidebarOpen}
            endType="PROCESS"
          />
        )}

        <Dialog
          visible={showDeleteProcessModal}
          onHide={() => setShowDeleteProcessModal(false)}
          className="delete-message-modal"
        >
          <div className="delete-message-container">
            <h3 className="header">
              Are you sure you want to delete this process?
            </h3>
            <p className="content">
              Deleting will remove the process and all related items from the
              user interface,
              <br />
              including messages, documents, calendar entries, and linked
              escalations.
            </p>
          </div>
          <div className="action-container">
            <CustomeButton
              label="Cancel"
              onClick={() => setShowDeleteProcessModal(false)}
              disabled={isDeletingProcess}
            />
            <CustomeButton
              label="Yes, Delete"
              variant="remove"
              loading={isDeletingProcess}
              onClick={handleConfirmDeleteProcess}
              disabled={isDeletingProcess}
            />
          </div>
        </Dialog>
        {showDeleteError && (
          <ErrorToast
            summary="Delete failed"
            detail={`${process?.name} process could not be deleted. Please try again.`}
            onClose={setShowDeleteError}
          />
        )}
        <NewRequestModal
          showModal={showRequestModal}
          setShowModal={setShowRequestModal}
          hideRequestModal={hideRequestModal}
          refetch={fetchProcessById}
          requestType={REQUEST_TYPES.ESCALATION}
        />
      </ProcessContext.Provider>
    </ProcessLayout>
  );
};

export default ProcessLayoutWrapper;
